import { ConfigContext as ConfigContext1 } from "@app/app/providers/ConfigContext.tsx";
import { GAME_SPECIFIC_PATHS } from "@app/app/routing/paths.ts";
import { useUrlQuery } from "@app/shared/lib/Hooks/useQuery.tsx";
import {
	FxIdDomainStoreEnumsSupportedWebPublishingPlatform,
	type FxIdWebFeaturesGamesChooseGamePortalConfig,
	type FxIdWebFeaturesGamesChooseGameResponsePublicGameData,
	queries
} from "@fxid/openapi-client";
import { IframeType, IframeTypeToSupportedPlatformMap } from "@fxid/sdk";
import { AppConfig } from "@fxid/shared";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ReactNode, useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";

/**
 * @fileoverview Этот файл реализует ConfigProvider — центральный компонент
 * для загрузки и предоставления конфигурации приложения (портала и игры).
 * Он использует React Suspense для декларативной обработки состояния загрузки.
 */

/**
 * @description Контекст для хранения конфигураций.
 */
export interface IConfigContext {
	game: string;
	portalConfig: FxIdWebFeaturesGamesChooseGamePortalConfig;
	gameConfig?: FxIdWebFeaturesGamesChooseGameResponsePublicGameData;
	platformData: {
		adapter: IframeType;
		platform: FxIdDomainStoreEnumsSupportedWebPublishingPlatform;
	};
}

/**
 * @description Провайдер, который загружает конфигурацию и передает ее дочерним компонентам.
 *              Приостанавливает рендеринг дочерних элементов до тех пор, пока
 *              основная конфигурация не будет загружена.
 */
export const ConfigProvider = ({ children }: { children: ReactNode }) => {
	const urlQuery = useUrlQuery();
	const location = useLocation();

	const staticGameName = useMemo(() => AppConfig.game ?? import.meta.env.VITE_APP_GAME_OVERRIDE, []);
	const staticAdapterName = useMemo(() => AppConfig.forcedAdapter, []);

	// Логика определения имени игры остается прежней.
	const gameNameFromPath = useMemo(() => {
		for (const path of GAME_SPECIFIC_PATHS) {
			const match = matchPath(path, location.pathname);
			if (match?.params.game) {
				return match.params.game;
			}
		}
		return undefined;
	}, [location.pathname]);

	const adapterNameFromPath = useMemo(() => {
		for (const path of GAME_SPECIFIC_PATHS) {
			const match = matchPath(path, location.pathname);
			if (match?.params.adapter) {
				return match.params.adapter;
			}
		}
		return undefined;
	}, [location.pathname]);

	const gameName = useMemo(() => {
		const result = staticGameName ?? gameNameFromPath ?? (urlQuery.has("game") ? urlQuery.get("game") : undefined);
		log.debug(
			"gameName memo. staticGameName: %s, gameNameFromPath: %s, urlQuery: %o",
			staticGameName,
			gameNameFromPath,
			urlQuery
		);
		log.info("Using %s game", result);
		return result;
	}, [staticGameName, gameNameFromPath, urlQuery.toString()]);

	const adapterName: IframeType = useMemo(() => {
		const adapterNameString =
			staticAdapterName ?? adapterNameFromPath ?? (urlQuery.has("adapter") ? urlQuery.get("adapter") : "fxid");

		const result = adapterNameString as IframeType;
		log.debug(
			"staticAdapterName memo. adapterNameFromPath: %s, gameNameFromPath: %s, urlQuery: %o",
			staticAdapterName,
			adapterNameFromPath,
			urlQuery
		);
		log.info("Using %s adapter", result);
		return result;
	}, [staticAdapterName, adapterNameFromPath, urlQuery.toString()]);

	const webPublishingPlatform: FxIdDomainStoreEnumsSupportedWebPublishingPlatform = useMemo(() => {
		let platform = FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Unspecified;
		if (adapterName !== undefined && IframeTypeToSupportedPlatformMap[adapterName] !== undefined) {
			platform = IframeTypeToSupportedPlatformMap[adapterName];
		}

		log.debug("webPublishingPlatform useMemo. adapterName %s", adapterName);
		log.info("Using %s platform", FxIdDomainStoreEnumsSupportedWebPublishingPlatform[platform]);
		return platform;
	}, [adapterName]);

	// 1. Используем `useSuspenseQuery` для основного запроса.
	// Он будет "приостанавливать" рендеринг, пока данные не будут готовы.
	// Ошибки будут перехвачены ближайшим ErrorBoundary.
	const { data: chooseGameData } = useSuspenseQuery({
		...queries.play.chooseGame({
			GameSystemName: (gameName ?? "").toString(),
			OriginUrl: window.location.origin,
			WebPublishingPlatform: webPublishingPlatform
		}),
		staleTime: Infinity,
		refetchOnWindowFocus: false
	});

	const { PortalConfig: portalConfig, PublicGameData: singleSiteGameConfig } = chooseGameData;

	// Собираем контекст: gameConfig берется либо из chooseGame (для сайта одной игры),
	// либо из отдельного запроса (для страницы игры на портале).
	const contextValue: IConfigContext = useMemo(
		() => ({
			game: (gameName ?? "").toString(),
			portalConfig,
			gameConfig: singleSiteGameConfig ?? undefined,
			platformData: {
				adapter: adapterName,
				platform: webPublishingPlatform
			}
		}),
		[portalConfig, singleSiteGameConfig, gameName, adapterName, webPublishingPlatform]
	);

	return <ConfigContext1 value={contextValue}>{children}</ConfigContext1>;
};
